import React, { useEffect, useState } from "react";
import {
  Card,
  Spin,
  message,
  Typography,
  Tag,
  Button,
  Modal,
  Form,
  Select,
  Space,
} from "antd";
import { useParams } from "react-router-dom";
import {
  getApplicationApi,
  updateApplicationApi,
} from "../../../apiservice/ats";

const { Paragraph } = Typography;

const STATUS_OPTIONS = [
  "Applied",
  "Screening",
  "Interview",
  "Offer",
  "Hired",
  "Rejected",
];
const STATUS_COLORS = {
  Applied: "blue",
  Screening: "purple",
  Interview: "orange",
  Offer: "gold",
  Hired: "green",
  Rejected: "red",
};

const ApplicationView = () => {
  const { id } = useParams();
  const [application, setApplication] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();

  // Load application
  useEffect(() => {
    const loadApplication = async () => {
      try {
        setLoading(true);
        const res = await getApplicationApi(id);
        if (!res.data) {
          message.error("Application not found");
          setApplication(null);
          return;
        }
        setApplication(res.data);
      } catch (err) {
        console.error(err);
        message.error("Failed to load application");
      } finally {
        setLoading(false);
      }
    };
    loadApplication();
  }, [id]);

  // Open edit modal
  const openEditModal = () => {
    form.setFieldsValue({ status: application.status });
    setEditModalVisible(true);
  };

  // Save new status
  const handleSaveStatus = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);

      await updateApplicationApi(application.id, { status: values.status });

      message.success("Application status updated");
      // manually update state to re-render
      setApplication({ ...application, status: values.status });
      setEditModalVisible(false);
    } catch (err) {
      console.error(err);
      message.error("Failed to update status");
    } finally {
      setSaving(false);
    }
  };

  if (loading)
    return <Spin size="large" style={{ display: "block", margin: 40 }} />;
  if (!application) return <div>Application not found</div>;

  // Current status index
  const currentIndex = STATUS_OPTIONS.indexOf(application.status);

  return (
    <Card
      title={`Application #${application.id}`}
      style={{ maxWidth: 700, margin: "40px auto" }}
    >
      {/* Status Transition with arrows */}
      <div
        style={{
          marginBottom: 20,
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
        }}
      >
        {STATUS_OPTIONS.map((status, idx) => {
          const isCurrent = status === application.status;
          return (
            <React.Fragment key={status}>
              <Tag
                color={isCurrent ? STATUS_COLORS[status] : "default"}
                style={{ fontWeight: isCurrent ? "bold" : "normal" }}
              >
                {status}
              </Tag>
              {idx < STATUS_OPTIONS.length - 1 && (
                <span style={{ margin: "0 8px", fontWeight: "bold" }}>→</span>
              )}
            </React.Fragment>
          );
        })}
      </div>

      <p>
        <strong>Candidate Name:</strong> {application.candidate_name}
      </p>
      <p>
        <strong>Email:</strong> {application.candidate_email}
      </p>
      <p>
        <strong>Phone:</strong> {application.candidate_phone || "-"}
      </p>
      <p>
        <strong>Experience:</strong>
      </p>
      <Paragraph>{application.experience || "-"}</Paragraph>

      <p>
        <strong>Job:</strong> {application.job_title}
      </p>
      <p>
        <strong>Status:</strong>{" "}
        <Tag color={STATUS_COLORS[application.status] || "default"}>
          {application.status}
        </Tag>{" "}
        <Button type="link" onClick={openEditModal}>
          Edit Status
        </Button>
      </p>

      {application.resume && (
        <p>
          <strong>Resume:</strong>{" "}
          <Button type="link" href={application.resume} target="_blank">
            View / Download
          </Button>
        </p>
      )}

      {application.cover_letter && (
        <p>
          <strong>Cover Letter:</strong>{" "}
          <Button type="link" href={application.cover_letter} target="_blank">
            View / Download
          </Button>
        </p>
      )}

      {/* Edit Status Modal */}
      <Modal
        title="Edit Application Status"
        open={editModalVisible}
        onCancel={() => setEditModalVisible(false)}
        onOk={handleSaveStatus}
        confirmLoading={saving}
        destroyOnClose
      >
        <Form form={form} layout="vertical">
          <Form.Item
            label="Status"
            name="status"
            rules={[{ required: true, message: "Please select a status" }]}
          >
            <Select placeholder="Select status">
              {STATUS_OPTIONS.map((status) => (
                <Select.Option key={status} value={status}>
                  {status}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
};

export default ApplicationView;
